import { useEffect, useState } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { MagnifyingGlass, ArrowRight, SignOut } from "@phosphor-icons/react";
import LoginPage from "./LoginPage";
import { rememberPortalSlug, forgetPortalSlug } from "../lib/tenantPortal";
import { HOST_SLUG, WORKSPACE_SLUG, ownWorkspaceUrl } from "../lib/workspace";
import { applyDoorHead } from "../lib/installPrompt";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Shell({ children, testId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8" data-testid={testId}>
      <div className="w-full max-w-md text-center animate-fade-in">
        <img src="/fixlr-logo-dark.png" alt="Fixlr" className="h-10 mx-auto mb-8 dark:hidden" />
        <img src="/fixlr-logo-light.png" alt="Fixlr" className="h-10 mx-auto mb-8 hidden dark:block" />
        {children}
      </div>
    </div>
  );
}

export default function TenantPortalPage() {
  const params = useParams();
  const slug = params.slug || WORKSPACE_SLUG || HOST_SLUG;
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [portal, setPortal] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPortal(null);
    setMissing(false);
    axios.get(`${BACKEND}/api/public/tenant-portal/${encodeURIComponent(slug)}`)
      .then(({ data }) => { setPortal(data); rememberPortalSlug(data.slug); })
      .catch(() => { forgetPortalSlug(); setMissing(true); });
  }, [slug]);

  // Make "Add to home screen" install *this tenant's* app (manifest, iOS icon/title, theme colour)
  useEffect(() => {
    if (!portal) return undefined;
    return applyDoorHead({ slug: portal.slug, companyName: portal.company_name, primaryColor: portal.primary_color });
  }, [portal]);

  if (missing) {
    return (
      <Shell testId="tenant-portal-missing">
        <div className="h-12 w-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center"><MagnifyingGlass size={22} className="text-muted-foreground" /></div>
        <h1 className="text-2xl font-bold font-[Chivo] tracking-tight mb-2">No workspace at this address</h1>
        <p className="text-sm text-muted-foreground mb-6">
          <span className="font-mono">{window.location.host}/{slug}</span> doesn't belong to any company on Fixlr. Check the link you were given, or sign in with your usual account.
        </p>
        <Button asChild data-testid="tenant-portal-missing-login"><a href="/login">Go to sign in <ArrowRight size={14} className="ml-1" /></a></Button>
      </Shell>
    );
  }

  if (!portal) {
    return <Shell testId="tenant-portal-loading"><p className="text-sm text-muted-foreground">Loading workspace…</p></Shell>;
  }

  if (user) {
    const next = searchParams.get("next");
    const safeNext = next && next.startsWith("/") && !next.startsWith("//") && next.split("?")[0] !== `/${slug}` && next.split("?")[0] !== "/login" ? next : "/";
    if (user.is_platform_admin || user.tenant_id === portal.tenant_id) return <Navigate to={safeNext} replace />;
    return (
      <Shell testId="tenant-portal-switch">
        <h1 className="text-2xl font-bold font-[Chivo] tracking-tight mb-2">You're signed in elsewhere</h1>
        <p className="text-sm text-muted-foreground mb-6">
          You're currently signed in as <span className="font-medium text-foreground">{user.email}</span>, which isn't a member of <span className="font-medium text-foreground">{portal.company_name}</span>. Sign out to continue to this workspace.
        </p>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={() => window.location.assign(ownWorkspaceUrl(user))} data-testid="tenant-portal-stay">Back to my workspace</Button>
          <Button onClick={async () => { await logout(); }} data-testid="tenant-portal-signout"><SignOut size={14} className="mr-1" /> Sign out</Button>
        </div>
      </Shell>
    );
  }

  return <LoginPage tenantPortal={portal} />;
}
